import { formatNumber, PROMPT_KEYS, PROMPTS, type SiteStats } from "@kmstories/shared";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Lockup } from "../components/Lockup.js";
import { TrailerFacade } from "../components/TrailerFacade.js";
import { TRAILER } from "../content/trailer.js";
import { useT } from "../i18n/LocaleProvider.js";
import { api } from "../lib/api.js";
import { useDocumentTitle } from "../lib/useDocumentTitle.js";

export function HomePage() {
  const { m, lp, locale } = useT();
  useDocumentTitle(m.meta.pages.home);
  const [stats, setStats] = useState<SiteStats | null>(null);
  useEffect(() => {
    api.get<SiteStats>("/api/stats").then(setStats).catch(() => {});
  }, []);

  return (
    <>
      <section className="hero">
        <div className="hero__art" aria-hidden="true" />
        <div className="hero__veil" aria-hidden="true" />
        <div className="container">
          <div className="hero__inner">
            <Lockup hero />
            <div className="hero__kicker">
              <span>{m.hero.kicker}</span>
              <span className="hero__status">{m.meta.statusBadge}</span>
            </div>
            <h1 className="hero__title">{m.hero.title}</h1>
            <p className="hero__lede">{m.hero.lede}</p>
            <div className="hero__actions">
              <Link className="btn btn--primary" to={lp("/share")}>
                {m.hero.ctaShare}
              </Link>
              <Link className="btn btn--ghost" to={lp("/films/my-child")}>
                {m.hero.ctaFilm}
              </Link>
              <a className="btn btn--ghost" href="#updates">
                {m.hero.ctaUpdates}
              </a>
            </div>
            <p className="hero__provenance">{m.meta.provenance}</p>
            {stats && stats.storyCount >= 5 && (
              <p className="hero__counter">{m.hero.counter.replace("{count}", formatNumber(stats.storyCount, locale))}</p>
            )}
          </div>
        </div>
      </section>

      <section className="section" id="about">
        <div className="thread" aria-hidden="true" />
        <div className="container">
          <p className="section__kicker">{m.nav.about}</p>
          <div className="about__grid">
            <div>
              <h2>{m.about.heading}</h2>
              <p>{m.about.p1}</p>
              <p>{m.about.p2}</p>
              <p>{m.about.p3}</p>
              <Link className="btn btn--primary" to={lp("/share")}>
                {m.about.cta}
              </Link>
            </div>
            <div className="about__aside">
              <figure className="figure">
                <img src="/art/tea-glass.jpg" alt="" width="1200" height="1200" loading="lazy" />
              </figure>
              <div className="card card--paper">
                <h3>{m.about.directorNoteHeading}</h3>
                <p style={{ margin: 0 }}>{m.about.directorNote}</p>
              </div>
              <div className="card">
                <h3>{m.about.howWeWorkHeading}</h3>
                <p className="muted" style={{ margin: 0 }}>
                  {m.about.howWeWork}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section section--tight" id="story">
        <div className="container">
          <p className="section__kicker">{m.storyBlock.heading}</p>
          <h2>{m.storyBlock.invitation}</h2>
          <p className="hero__lede">{m.storyBlock.body}</p>
          <div className="pills" style={{ marginBlockEnd: "1.75rem" }}>
            <span className="pill">{m.storyBlock.private}</span>
            <span className="pill">{m.storyBlock.anonymous}</span>
            <span className="pill">{m.storyBlock.sweden}</span>
          </div>
          <h3 className="muted" style={{ fontSize: "1rem", fontWeight: 600 }}>
            {m.storyBlock.promptsHeading}
          </h3>
          <div className="prompts">
            {PROMPT_KEYS.map((k) => (
              <Link key={k} className="prompt" to={`${lp("/share")}?prompt=${k}`}>
                <span className="prompt__mark" aria-hidden="true">
                  ❝
                </span>
                {PROMPTS[k][locale]}
              </Link>
            ))}
          </div>
          <div style={{ marginBlockStart: "1.75rem" }}>
            <Link className="btn btn--primary" to={lp("/share")}>
              {m.storyBlock.cta}
            </Link>
          </div>
        </div>
      </section>

      <section className="section section--tight" id="updates">
        <div className="container">
          <p className="section__kicker">{m.trailer.heading}</p>
          <div className="about__grid">
            <div>
              {/* The film page warns before its trailer; the home page must too, or this is the one door with no warning on it. */}
              <p className="muted small" role="note">{m.trailer.warning}</p>
              <TrailerFacade youtubeId={TRAILER.youtubeId} thumbnailUrl={TRAILER.poster} title={m.trailer.heading} />
            </div>
            <div className="about__aside">
              <h2>{m.film.title}</h2>
              <p className="muted">{m.film.logline}</p>
              <p className="muted small">{m.hero.emailHint}</p>
              <Link className="btn btn--ghost" to={lp("/films/my-child")}>
                {m.nav.film}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="section section--tight">
        <div className="container">
          <div className="teasers">
            <div className="card">
              <p className="section__kicker">{m.nav.support}</p>
              <h3>{m.support.heading}</h3>
              <p className="muted">
                <strong>{m.support.honest}</strong> {m.support.early}
              </p>
              <Link className="btn btn--ghost" to={lp("/support")}>
                {m.support.formHeading}
              </Link>
            </div>
            <div className="card">
              <p className="section__kicker">{m.nav.contact}</p>
              <h3>{m.contact.heading}</h3>
              <p className="muted">{m.contact.topics.casting}</p>
              <Link className="btn btn--ghost" to={lp("/contact")}>
                {m.nav.contact}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
